/**
 * Ad-hoc urenregistratie buiten de agenda (migratie 163).
 *
 * Professional: /api/professional/adhoc-uren
 * Publiek (SMS-/e-mailtoken, geen login): /api/adhoc-akkoord/:token
 */
import api from './api';

const d = <T,>(r: { data: { data: T } }) => r.data.data;
const PROF = '/professional/adhoc-uren';

export type AdhocKanaal = 'SMS' | 'EMAIL';
export type AdhocStatus =
  | 'PENDING_CLIENT_CONFIRMATION'
  | 'PENDING_EMAIL_APPROVAL'
  | 'APPROVED_RELEASED'
  | 'REJECTED'
  | 'WITHDRAWN'
  | 'INVOICED';

/** Zelfde sets als src/constants/adhocUren.ts — de backend weigert alles daarbuiten. */
export const ADHOC_CATEGORIEEN: Record<AdhocKanaal, Record<string, string>> = {
  SMS: {
    crisis_nazorg: 'Crisisnazorg (bijv. avond- of weekendtelefoontje)',
    telefonische_opvolging: 'Onverwachte telefonische opvolging',
    escalatiedossier: 'Schrijven van een escalatiedossier',
  },
  EMAIL: {
    overleg_leidinggevende: 'Overleg met teamleider of afdelingshoofd',
    overleg_preventiemedewerker: 'Overleg met preventiemedewerker',
    workshop_intern: 'Interne workshop of teamsessie',
  },
};

export interface AdhocWerkgever {
  employer_id: string;
  organisatie: string;
  zakelijk_domein: string | null;
}

export interface AdhocRegistratie {
  id: string;
  employer_id: string;
  organisatie: string | null;
  kanaal: AdhocKanaal;
  categorie: string;
  categorie_label: string;
  datum: string;
  tijd_minuten: number;
  uurtarief: string;
  bedrag: string;
  toelichting: string | null;
  transactie_id: string;
  contact_email: string | null;
  telefoon_hint: string | null;
  status: AdhocStatus;
  afwijzing_reden: string | null;
  beoordeeld_op: string | null;
  verlopen: boolean;
  created_at: string;
}

export interface AdhocInvoer {
  employer_id: string;
  kanaal: AdhocKanaal;
  categorie: string;
  datum: string;
  tijd_minuten: number;
  toelichting?: string;
  telefoon?: string;
  contact_email?: string;
}

export const getAdhocWerkgevers = () => api.get<{ data: AdhocWerkgever[] }>(`${PROF}/werkgevers`).then(d);
export const getAdhocUren = () => api.get<{ data: AdhocRegistratie[] }>(PROF).then(d);
export const registreerAdhocUren = (body: AdhocInvoer) => api.post<{ data: AdhocRegistratie }>(PROF, body).then(d);
export const trekAdhocIn = (id: string) => api.post<{ data: AdhocRegistratie }>(`${PROF}/${id}/intrekken`).then(d);
export const verstuurAdhocOpnieuw = (id: string, telefoon?: string) =>
  api.post<{ data: AdhocRegistratie }>(`${PROF}/${id}/opnieuw`, { telefoon: telefoon ?? null }).then(d);

// Publiek
export interface AdhocAkkoordVerzoek {
  kanaal: AdhocKanaal;
  categorie_label: string;
  datum: string;
  tijd_minuten: number;
  toelichting: string | null;
  professional_naam: string;
  organisatie: string | null;
  /** Management-update uit het gespreksverslag (migratie 176/181); alleen bij SMS. */
  deel5: { arbo_kader: string; fte_belastbaarheid: number | null; verwachte_verzuimreductie: string | null; werkvloer_advies: string } | null;
}
const PUB = (token: string) => `/adhoc-akkoord/${encodeURIComponent(token)}`;
export const getAdhocAkkoord = (token: string) => api.get<{ data: AdhocAkkoordVerzoek }>(PUB(token)).then(d);
export const beoordeelAdhoc = (token: string, besluit: 'akkoord' | 'afwijzen', reden?: string, deel5Delen = false) =>
  api.post<{ data: { status: AdhocStatus } }>(PUB(token), { besluit, reden: reden || null, deel5_delen: deel5Delen }).then(d);

export const duurLabel = (minuten: number) => {
  const u = Math.floor(minuten / 60);
  const m = minuten % 60;
  return u && m ? `${u} u ${m} min` : u ? `${u} uur` : `${m} min`;
};
/** YYYY-MM-DD → "8 oktober 2026", zonder tijdzoneverschuiving. */
export const datumLabel = (datum: string) =>
  new Date(`${datum.slice(0, 10)}T12:00:00Z`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
